import { useEffect, useState } from 'react'
import {
  DROPDOWNS,
  NON_B2C_KEYS,
} from '../../setupWizard/CustomerProfileSetup/dropdowns'
import { useStore } from '../../store'
import { ALERTS_ACTIONS } from '../../store/alerts'
import { authedAxios } from '../../utils'
import useBusinessTypes from './useBusinessTypes'
import useCompanyConfig from './useCompanyConfig'

const useDropdowns = (options = {}) => {
  const [dropdownsSummary, setDropdownsSummary] = useState([])
  const [dropdownsSummaryCopy, setDropdownsSummaryCopy] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [hasLoadError, setHasLoadError] = useState(false)
  const [hasSubmitError, setHasSubmitError] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { dispatch } = useStore()

  const {
    companyConfigStoreCopy: companyConfig,
    updateCompanyConfig: updateCompanyConfigIN,
  } = useCompanyConfig()
  const { businessTypes } = useBusinessTypes()

  const updateCompanyConfig = (...args) => {
    return updateCompanyConfigIN(...args)
  }

  const businessType = businessTypes.find(
    (bt) => bt.id === companyConfig?.businessTypeId
  )

  const abbreviation = businessType?.abbreviation
  const isB2C = abbreviation === 'B2C'

  const filterSummary = (d) => !isB2C || !NON_B2C_KEYS.includes(d.key)

  const reset = () =>
    setDropdownsSummary(dropdownsSummaryCopy.filter(filterSummary))

  // THIS COULD BE FIXED BY JUST ADDING THESE DEFAULT VALUES TO THE DB MIGRATION RATHER THAN KEEPING THEM IN THE FRONTEND

  const getDropdowns = () => {
    if (!companyConfig || !businessTypes) return Promise.resolve()

    return authedAxios
      .get('/api/custom-fields/dropdowns/summary')
      .then((res) => {
        const summary =
          companyConfig.hasSavedDropdowns || options.noFallback
            ? res.data.summary
            : res.data.summary.map((dd) => {
                const defaultValue = DROPDOWNS.find(
                  (defaultDD) => defaultDD.key === dd.key
                )

                return {
                  ...dd,
                  options: defaultValue?.options || [],
                }
              })

        setDropdownsSummaryCopy(summary)
        setDropdownsSummary(summary.filter(filterSummary))
      })
      .catch((e) => {
        setHasLoadError(true)
        dispatch({
          type: ALERTS_ACTIONS.CREATE_ALERT,
          alert: {
            message: 'Failed trying to load dropdowns',
            severity: 'error',
          },
        })
      })
      .finally(() => setIsLoading(false))
  }

  const saveDropdowns = () => {
    setIsSubmitting(true)

    return authedAxios
      .put('/api/custom-fields/dropdowns', dropdownsSummary)
      .then((res) => {
        return Promise.all([
          res,
          updateCompanyConfig({
            ...companyConfig,
            hasSavedDropdowns: true,
          }),
        ])
      })
      .catch((e) => {
        setHasSubmitError(true)
        dispatch({
          type: ALERTS_ACTIONS.CREATE_ALERT,
          alert: {
            message: 'Failed trying to save dropdowns',
            severity: 'error',
          },
        })

        throw e
      })
      .finally((...promises) => {
        setIsSubmitting(false)
        return promises
      })
  }

  useEffect(() => {
    if (!abbreviation) return
    setDropdownsSummary(dropdownsSummaryCopy.filter(filterSummary))
  }, [isB2C, abbreviation, companyConfig])

  useEffect(() => {
    getDropdowns()
  }, [companyConfig, businessTypes])

  return {
    dropdownsSummary,
    setDropdownsSummary,
    dropdownsSummaryCopy,
    setDropdownsSummaryCopy,
    isLoading,
    hasLoadError,
    hasSubmitError,
    filterSummary,
    saveDropdowns,
    isSubmitting,
    reset,
  }
}

export default useDropdowns
